import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-google-places-autocomplete';
import colors from '../../constants/colors';
import {
  OnboardingBoxStep2,
  OnboardingTitle,
  StepIndicator,
  OnboardingFormContainer,
  NavigationSection,
  BackButton,
  NextButton,
  FormGrid,
  FormField,
  FieldGroup,
  FieldLabel,
  StyledInput,
  CheckboxOption,
  CheckboxInput,
  CheckboxLabel,
  DeliveryMethodContainer,
  DeliveryMethodRow,
  DeliveryMethodRowContent,
  MinPurchaseContainer,
  MinPurchaseInputContainer,
  MinPurchaseInput,
  MinPurchaseLabel,
  DollarSymbol,
  CollapsibleDeliveryAreaSection,
  HelperText,
  ErrorText,
  Form,
  AddressContainer,
  AddressSearchContainer,
  AddressDetailsGrid,
  ReadOnlyAddressField,
  ReadOnlyAddressLabel,
  FormFieldContainer,
  FormFieldInput,
  FormFieldLabel,
  AddressHelperText,
  AddressSearchRow,
  ZipSearchContainer,
  ZipSearchInput,
  ZipSuggestionsList,
  ZipSuggestionItem,
  AddressDetailsToggleContainer,
  AddressDetailsToggleLabel,
  AddressDetailsToggleIcon,
  ValidationAlertIcon,
  AddressDetailsToggleLabelContainer,
  ErrorContainer,
  CollapsibleAddressDetailsSection,
  ClearAddressButton,
  InputWithClearContainer,
  ModalOverlay,
  ModalContainer,
  ModalTitle,
  ModalMessage,
  ModalActions,
  ModalCancelButton,
  ModalConfirmButton,
} from './Onboarding.styled';

const PLACES_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyCiXwAYG6cAHjDXFaHxNcWueIaVZ1X2CQ0';

export const OnboardingBranchStore = ({
  onNext,
  onBack,
  initialData = {},
  currentBranchIndex = 0,
  totalBranches = 1,
  isLastBranch = true,
  branchStoreId = null,
}) => {
  const { t } = useTranslation();
  const [addressValue, setAddressValue] = useState(null);
  const [fullAddress, setFullAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [latLng, setLatLng] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [zipSearchValue, setZipSearchValue] = useState('');
  const [zipSuggestions, setZipSuggestions] = useState([]);

  const [showAddressDetails, setShowAddressDetails] = useState(true);

  const [showDeliveryArea, setShowDeliveryArea] = useState(
    initialData.hasDelivery || false,
  );

  const [showClearModal, setShowClearModal] = useState(false);

  const [errors, setErrors] = useState({});

  const [pickupInputEnabled, setPickupInputEnabled] = useState(
    initialData.hasPickup || false,
  );
  const [deliveryInputEnabled, setDeliveryInputEnabled] = useState(
    initialData.hasDelivery || false,
  );

  const [formData, setFormData] = useState({
    branchStoreName: initialData.branchStoreName || '',
    branchStoreEmail: initialData.branchStoreEmail || '',
    branchStorePhone: initialData.branchStorePhone || '',
    hasPickup: initialData.hasPickup || false,
    hasDelivery: initialData.hasDelivery || false,
    pickupMinAmount: initialData.pickupMinAmount || '15.00',
    deliveryMinAmount: initialData.deliveryMinAmount || '15.00',
    deliveryRadius: initialData.deliveryRadius || '5',
    addressName: initialData.addressName || '',
    addressDescription: initialData.addressDescription || '',

    ...initialData,
  });

  const validateAlphanumeric = (value, maxLength = 55) => {
    if (!value) return t('Onboarding.validation.required');
    if (value.length > maxLength)
      return t('Onboarding.validation.maxLength', { maxLength });
    if (!/^[a-zA-Z0-9\s]+$/.test(value))
      return t('Onboarding.validation.alphanumeric');
    return '';
  };

  const validateEmail = (value, maxLength = 55) => {
    if (!value) return t('Onboarding.validation.required');
    if (value.length > maxLength)
      return t('Onboarding.validation.maxLength', { maxLength });
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value))
      return t('Onboarding.validation.email');
    return '';
  };

  const validatePhone = (value, maxLength = 15) => {
    if (!value) return '';
    if (value.length > maxLength)
      return t('Onboarding.validation.phoneMaxLength', { maxLength });
    if (!/^\d+$/.test(value)) return t('Onboarding.validation.phone');
    if (value.length < 7) return t('Onboarding.validation.phoneMinLength');
    return '';
  };

  const validateZip = (value, maxLength = 10) => {
    if (!value) return '';
    if (value.length > maxLength)
      return t('Onboarding.validation.zipMaxLength', { maxLength });
    if (!/^\d+(-\d+)?$/.test(value)) return t('Onboarding.validation.zip');
    if (value.length < 5) return t('Onboarding.validation.zipMinLength');
    return '';
  };

  const validateAddress = () => {
    if (!fullAddress) return t('Onboarding.validation.address');
    return '';
  };

  const validateDeliveryMethods = () => {
    if (!formData.hasPickup && !formData.hasDelivery) {
      return t('Onboarding.validation.deliveryMethods');
    }
    return '';
  };

  const hasAddressDetailsErrors = () => {
    return !!(errors.addressName || errors.addressDescription);
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateAlphanumeric(formData.branchStoreName, 55);
    if (nameError) newErrors.branchStoreName = nameError;

    const emailError = validateEmail(formData.branchStoreEmail, 55);
    if (emailError) newErrors.branchStoreEmail = emailError;

    const phoneError = validatePhone(formData.branchStorePhone, 15);
    if (phoneError) newErrors.branchStorePhone = phoneError;

    const zipError = validateZip(zipSearchValue, 10);
    if (zipError) newErrors.zipCode = zipError;

    const addressError = validateAddress();
    if (addressError) newErrors.address = addressError;

    if (formData.addressName) {
      const addressNameError = validateAlphanumeric(formData.addressName, 55);
      if (addressNameError) newErrors.addressName = addressNameError;
    }

    if (formData.addressDescription) {
      const addressDescError = validateAlphanumeric(
        formData.addressDescription,
        55,
      );
      if (addressDescError) newErrors.addressDescription = addressDescError;
    }

    const deliveryError = validateDeliveryMethods();
    if (deliveryError) newErrors.deliveryMethods = deliveryError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMinPurchaseKeyDown = (field) => (event) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();

      const currentValue = parseFloat(formData[field]) || 0;
      const increment = event.shiftKey ? 10 : 1;
      let newValue;

      if (event.key === 'ArrowUp') {
        newValue = Math.min(currentValue + increment, 500);
      } else {
        newValue = Math.max(currentValue - increment, 0);
      }

      const formattedValue =
        newValue % 1 === 0 ? newValue.toString() : newValue.toFixed(2);

      setFormData({
        ...formData,
        [field]: formattedValue,
      });
    }
  };

  const handleMinPurchaseClick = (field) => () => {
    if (field === 'pickupMinAmount') {
      setPickupInputEnabled(true);
      if (!formData.hasPickup) {
        setFormData((prev) => ({
          ...prev,
          hasPickup: true,
        }));
      }
    } else if (field === 'deliveryMinAmount') {
      setDeliveryInputEnabled(true);
      if (!formData.hasDelivery) {
        setFormData((prev) => ({
          ...prev,
          hasDelivery: true,
        }));
        setShowDeliveryArea(true);
      }
    }
  };

  const handleMinPurchaseChange = (field) => (event) => {
    let value = event.target.value;

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return;
    }

    if (numValue < 0) {
      value = '0.00';
    } else if (numValue > 500) {
      value = '500.00';
    }

    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleMinPurchaseBlur = (field) => (event) => {
    let value = event.target.value;
    const numValue = parseFloat(value) || 20;

    const formattedValue = numValue.toFixed(2);

    setFormData({
      ...formData,
      [field]: formattedValue,
    });
  };

  const handleInputChange = (field) => (event) => {
    let value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

    if (field === 'branchStorePhone' && typeof value === 'string') {
      value = value.replace(/\D/g, '');
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }

    if (
      (field === 'hasPickup' || field === 'hasDelivery') &&
      value &&
      errors.deliveryMethods
    ) {
      setErrors((prev) => ({
        ...prev,
        deliveryMethods: '',
      }));
    }

    let updatedFormData = { ...formData, [field]: value };

    if (field === 'hasPickup') {
      setPickupInputEnabled(value);
      if (!value) {
        updatedFormData.pickupMinAmount = '15.00';
      }
    } else if (field === 'hasDelivery') {
      setDeliveryInputEnabled(value);
      setShowDeliveryArea(value);
      if (!value) {
        updatedFormData.deliveryMinAmount = '15.00';
      }
    }

    setFormData(updatedFormData);
  };

  const handleAddressSelect = async (address) => {
    setAddressValue(address);
    const results = await geocodeByAddress(address.label);
    const latLngData = await getLatLng(results[0]);
    setLatLng(latLngData);

    const aComponents = results[0].address_components;
    setFullAddress(address.label);

    const localityComponent = aComponents.find((c) =>
      c.types.includes('locality'),
    );
    setCity(localityComponent ? localityComponent.long_name : '');

    const adminAreaComponent = aComponents.find((c) =>
      c.types.includes('administrative_area_level_1'),
    );
    setState(adminAreaComponent ? adminAreaComponent.long_name : '');

    const postalCodeComponent = aComponents.find((c) =>
      c.types.includes('postal_code'),
    );
    const extractedZip = postalCodeComponent
      ? postalCodeComponent.long_name
      : '';

    setZipcode(extractedZip);
    setZipSearchValue(extractedZip);

    if (errors.address) {
      setErrors((prev) => ({
        ...prev,
        address: '',
      }));
    }
  };

  const handleZipSearch = async (zipValue) => {
    const filteredZip = zipValue.replace(/[^\d-]/g, '');
    setZipSearchValue(filteredZip);

    if (errors.zipCode) {
      setErrors((prev) => ({
        ...prev,
        zipCode: '',
      }));
    }

    if (filteredZip.length >= 3) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          {
            componentRestrictions: {
              postalCode: filteredZip,
              country: 'US',
            },
          },
          (results, status) => {
            if (status === 'OK' && results.length > 0) {
              const suggestions = results.slice(0, 5).map((result) => ({
                label: result.formatted_address,
                value: result,
              }));
              setZipSuggestions(suggestions);
            } else {
              setZipSuggestions([]);
            }
          },
        );
      } catch (error) {
        setZipSuggestions([]);
      }
    } else {
      setZipSuggestions([]);
    }
  };

  const handleZipSuggestionSelect = async (suggestion) => {
    const result = suggestion.value;
    const latLngData = {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    };
    setLatLng(latLngData);

    const aComponents = result.address_components;
    setFullAddress(result.formatted_address);

    const zipLocalityComponent = aComponents.find((c) =>
      c.types.includes('locality'),
    );
    setCity(zipLocalityComponent ? zipLocalityComponent.long_name : '');

    const zipAdminAreaComponent = aComponents.find((c) =>
      c.types.includes('administrative_area_level_1'),
    );
    setState(zipAdminAreaComponent ? zipAdminAreaComponent.long_name : '');

    const zipPostalCodeComponent = aComponents.find((c) =>
      c.types.includes('postal_code'),
    );
    const extractedZip = zipPostalCodeComponent
      ? zipPostalCodeComponent.long_name
      : '';

    setZipcode(extractedZip);
    setZipSearchValue(extractedZip);

    setZipSuggestions([]);

    setAddressValue({ label: result.formatted_address, value: result });

    if (errors.address) {
      setErrors((prev) => ({
        ...prev,
        address: '',
      }));
    }
  };

  const toggleAddressDetails = () => {
    setShowAddressDetails(!showAddressDetails);
  };

  const handleClearAddressRequest = () => {
    setShowClearModal(true);
  };

  const confirmClearAddress = () => {
    setAddressValue(null);
    setFullAddress('');
    setCity('');
    setState('');
    setZipcode('');
    setLatLng(null);
    setZipSearchValue('');
    setZipSuggestions([]);

    setFormData((prev) => ({
      ...prev,
      addressName: '',
      addressDescription: '',
    }));

    setShowClearModal(false);
    setShowAddressDetails(true);
  };

  const cancelClearAddress = () => {
    setShowClearModal(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    if (validateForm()) {
      try {
        const submissionData = {
          ...formData,
          fullAddress,
          city,
          state,
          zipcode,
          latLng,
        };

        await new Promise((resolve) => setTimeout(resolve, 500));

        onNext(submissionData);
      } catch (error) {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  return (
    <OnboardingBoxStep2>
      <OnboardingTitle>{t('Onboarding.branchStore.title')}</OnboardingTitle>

      <StepIndicator>
        {totalBranches > 1
          ? t('Onboarding.stepIndicator.branchStore', {
              current: currentBranchIndex + 1,
              total: totalBranches,
            })
          : t('Onboarding.stepIndicator.step2', {
              totalSteps: 1 + totalBranches,
            })}
      </StepIndicator>

      <OnboardingFormContainer>
        <Form onSubmit={handleSubmit}>
          <FormGrid>
            {/* Branch Store Name - Left Column */}
            <FormField>
              <FieldGroup>
                <FieldLabel>
                  {t('Onboarding.branchStore.branchStoreName')}
                </FieldLabel>
                <StyledInput
                  type="text"
                  placeholder={t(
                    'Onboarding.branchStore.branchStoreNamePlaceholder',
                  )}
                  name="branchStoreName"
                  value={formData.branchStoreName}
                  onChange={handleInputChange('branchStoreName')}
                  maxLength="55"
                  required
                />
                {errors.branchStoreName && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errors.branchStoreName}</ErrorText>
                  </ErrorContainer>
                )}
              </FieldGroup>
            </FormField>

            {/* Branch Store Email - Right Column */}
            <FormField>
              <FieldGroup>
                <FieldLabel>
                  {t('Onboarding.branchStore.branchStoreEmail')}
                </FieldLabel>
                <StyledInput
                  type="email"
                  placeholder={t(
                    'Onboarding.branchStore.branchStoreEmailPlaceholder',
                  )}
                  name="branchStoreEmail"
                  value={formData.branchStoreEmail}
                  onChange={handleInputChange('branchStoreEmail')}
                  maxLength="55"
                  required
                />
                {errors.branchStoreEmail && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errors.branchStoreEmail}</ErrorText>
                  </ErrorContainer>
                )}
              </FieldGroup>
            </FormField>

            {/* Branch Store Address - Full Width */}
            <FormField className="full-width">
              <FieldGroup>
                <FieldLabel>
                  {t('Onboarding.branchStore.branchStoreAddress')}
                </FieldLabel>

                <AddressContainer hasAddress={fullAddress}>
                  <AddressSearchRow>
                    <AddressSearchContainer>
                      <FormFieldLabel>
                        {t('Onboarding.branchStore.searchAddress')}
                      </FormFieldLabel>
                      <InputWithClearContainer
                        showClear={addressValue !== null}>
                        <GooglePlacesAutocomplete
                          apiKey={PLACES_API_KEY}
                          selectProps={{
                            value: addressValue,
                            onChange: handleAddressSelect,
                            placeholder: t(
                              'Onboarding.branchStore.searchAddressPlaceholder',
                            ),
                            openMenuOnClick: false,
                            openMenuOnFocus: false,
                            minInputLength: 1,
                            noOptionsMessage: () => null,
                            components: {
                              DropdownIndicator: () => null,
                              IndicatorSeparator: () => null,
                              NoOptionsMessage: () => null,
                            },
                            styles: {
                              control: (provided, state) => ({
                                ...provided,
                                width: '100%',
                                padding: '0px',
                                paddingRight: '40px',
                                paddingLeft: '4px',
                                border: `1px solid ${
                                  state.isFocused
                                    ? colors.inputFocusRed
                                    : colors.inputBorderGray
                                }`,
                                borderRadius: '4px',
                                fontSize: '16px',
                                fontFamily: 'Mulish',
                                minHeight: '48px',
                                boxShadow: state.isFocused
                                  ? `0 0 0 2px ${colors.inputFocusRedAlpha}`
                                  : 'none',
                                transition:
                                  'border-color 0.3s ease, box-shadow 0.3s ease',
                                cursor: 'text',
                                '&:hover': {
                                  borderColor: colors.inputHoverGray,
                                },
                              }),
                              input: (provided) => ({
                                ...provided,
                                fontSize: '16px',
                                fontFamily: 'Mulish',
                                margin: '0px',
                                paddingTop: '0px',
                                paddingBottom: '0px',
                              }),
                              placeholder: (provided) => ({
                                ...provided,
                                color: colors.inputPlaceholderGray,
                                fontSize: '16px',
                                fontFamily: 'Mulish',
                              }),
                              singleValue: (provided) => ({
                                ...provided,
                                fontSize: '16px',
                                fontFamily: 'Mulish',
                                color: colors.inputTextGray,
                              }),
                              valueContainer: (provided) => ({
                                ...provided,
                                padding: '8px 12px',
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                                border: `1px solid ${colors.inputBorderGray}`,
                                borderRadius: '4px',
                                boxShadow: `0 4px 8px ${colors.inputMenuShadow}`,
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                fontSize: '16px',
                                fontFamily: 'Mulish',
                                backgroundColor: state.isHighlighted
                                  ? colors.inputHighlightBg
                                  : colors.white,
                                color: colors.inputTextGray,
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: colors.inputHighlightBg,
                                },
                              }),
                            },
                          }}
                        />
                        {addressValue !== null && (
                          <ClearAddressButton
                            type="button"
                            onClick={handleClearAddressRequest}
                            title={t('Onboarding.branchStore.clearAddress')}
                          />
                        )}
                      </InputWithClearContainer>
                      <AddressHelperText>
                        {t('Onboarding.branchStore.addressHelper')}
                      </AddressHelperText>
                    </AddressSearchContainer>

                    <ZipSearchContainer>
                      <FormFieldLabel>
                        {t('Onboarding.branchStore.searchZip')}
                      </FormFieldLabel>
                      <InputWithClearContainer
                        showClear={zipSearchValue.length > 0}>
                        <ZipSearchInput
                          type="text"
                          placeholder={t(
                            'Onboarding.branchStore.searchZipPlaceholder',
                          )}
                          value={zipSearchValue}
                          onChange={(e) => handleZipSearch(e.target.value)}
                          maxLength="10"
                          pattern="[0-9\-]*"
                          inputMode="numeric"
                        />
                        {zipSearchValue.length > 0 && (
                          <ClearAddressButton
                            type="button"
                            onClick={handleClearAddressRequest}
                            title={t('Onboarding.branchStore.clearAddress')}
                          />
                        )}
                      </InputWithClearContainer>
                      {errors.zipCode && (
                        <ErrorContainer>
                          <ValidationAlertIcon>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </ValidationAlertIcon>
                          <ErrorText>{errors.zipCode}</ErrorText>
                        </ErrorContainer>
                      )}
                      {zipSuggestions.length > 0 && (
                        <ZipSuggestionsList>
                          {zipSuggestions.map((suggestion, index) => (
                            <ZipSuggestionItem
                              key={index}
                              onClick={() =>
                                handleZipSuggestionSelect(suggestion)
                              }>
                              {suggestion.label}
                            </ZipSuggestionItem>
                          ))}
                        </ZipSuggestionsList>
                      )}
                      <AddressHelperText>
                        {t('Onboarding.branchStore.zipHelper')}
                      </AddressHelperText>
                    </ZipSearchContainer>
                  </AddressSearchRow>

                  {errors.address && (
                    <ErrorContainer>
                      <ValidationAlertIcon>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                      </ValidationAlertIcon>
                      <ErrorText>{errors.address}</ErrorText>
                    </ErrorContainer>
                  )}

                  {fullAddress && (
                    <>
                      <AddressDetailsToggleContainer
                        isExpanded={showAddressDetails}
                        onClick={toggleAddressDetails}>
                        <AddressDetailsToggleLabelContainer>
                          <AddressDetailsToggleLabel
                            isExpanded={showAddressDetails}>
                            {showAddressDetails
                              ? t('Onboarding.branchStore.hideAddressDetails')
                              : t('Onboarding.branchStore.showAddressDetails')}
                          </AddressDetailsToggleLabel>
                          {!showAddressDetails && hasAddressDetailsErrors() && (
                            <ValidationAlertIcon>
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                            </ValidationAlertIcon>
                          )}
                        </AddressDetailsToggleLabelContainer>
                        <AddressDetailsToggleIcon
                          isExpanded={showAddressDetails}
                        />
                      </AddressDetailsToggleContainer>

                      <CollapsibleAddressDetailsSection
                        isVisible={showAddressDetails}>
                        {/* Address Name - At the top of complementary fields */}
                        <FormFieldContainer>
                          <FormFieldLabel>
                            {t('Onboarding.branchStore.addressName')}
                          </FormFieldLabel>
                          <FormFieldInput
                            type="text"
                            placeholder={t(
                              'Onboarding.branchStore.addressNamePlaceholder',
                            )}
                            value={formData.addressName}
                            onChange={handleInputChange('addressName')}
                            maxLength="55"
                          />
                          {errors.addressName && (
                            <ErrorContainer>
                              <ValidationAlertIcon>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                              </ValidationAlertIcon>
                              <ErrorText>{errors.addressName}</ErrorText>
                            </ErrorContainer>
                          )}
                          <AddressHelperText>
                            {t('Onboarding.branchStore.addressNameHelper')}
                          </AddressHelperText>
                        </FormFieldContainer>

                        <FormFieldContainer className="last-field">
                          <FormFieldLabel>
                            {t('Onboarding.branchStore.addressDescription')}
                          </FormFieldLabel>
                          <FormFieldInput
                            type="text"
                            placeholder={t(
                              'Onboarding.branchStore.addressDescriptionPlaceholder',
                            )}
                            value={formData.addressDescription}
                            onChange={handleInputChange('addressDescription')}
                            maxLength="55"
                          />
                          {errors.addressDescription && (
                            <ErrorContainer>
                              <ValidationAlertIcon>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                              </ValidationAlertIcon>
                              <ErrorText>{errors.addressDescription}</ErrorText>
                            </ErrorContainer>
                          )}
                          <AddressHelperText>
                            {t(
                              'Onboarding.branchStore.addressDescriptionHelper',
                            )}
                          </AddressHelperText>
                        </FormFieldContainer>

                        <AddressDetailsGrid>
                          <div>
                            <ReadOnlyAddressLabel>
                              {t('Onboarding.branchStore.fullAddress')}
                            </ReadOnlyAddressLabel>
                            <ReadOnlyAddressField>
                              {fullAddress}
                            </ReadOnlyAddressField>
                          </div>

                          <div>
                            <ReadOnlyAddressLabel>
                              {t('Onboarding.branchStore.city')}
                            </ReadOnlyAddressLabel>
                            <ReadOnlyAddressField>
                              {city || t('Onboarding.branchStore.notSpecified')}
                            </ReadOnlyAddressField>
                          </div>

                          <div>
                            <ReadOnlyAddressLabel>
                              {t('Onboarding.branchStore.state')}
                            </ReadOnlyAddressLabel>
                            <ReadOnlyAddressField>
                              {state ||
                                t('Onboarding.branchStore.notSpecified')}
                            </ReadOnlyAddressField>
                          </div>

                          <div>
                            <ReadOnlyAddressLabel>
                              {t('Onboarding.branchStore.zipCode')}
                            </ReadOnlyAddressLabel>
                            <ReadOnlyAddressField>
                              {zipcode ||
                                t('Onboarding.branchStore.notSpecified')}
                            </ReadOnlyAddressField>
                          </div>
                        </AddressDetailsGrid>
                      </CollapsibleAddressDetailsSection>
                    </>
                  )}
                </AddressContainer>
              </FieldGroup>
            </FormField>

            {/* Delivery Method Section - Full Width */}
            <FormField>
              <FieldGroup>
                <FieldLabel>
                  {t('Onboarding.branchStore.deliveryMethod')}
                </FieldLabel>

                <DeliveryMethodContainer>
                  <DeliveryMethodRow>
                    <CheckboxOption>
                      <CheckboxInput
                        type="checkbox"
                        name="hasPickup"
                        checked={formData.hasPickup}
                        onChange={handleInputChange('hasPickup')}
                      />
                      <CheckboxLabel>
                        {t('Onboarding.branchStore.pickup')}
                      </CheckboxLabel>
                    </CheckboxOption>
                    <MinPurchaseContainer>
                      <MinPurchaseInputContainer
                        disabled={!pickupInputEnabled}
                        onClick={handleMinPurchaseClick('pickupMinAmount')}>
                        <DollarSymbol
                          disabled={!pickupInputEnabled}
                          readOnly={!pickupInputEnabled}>
                          $
                        </DollarSymbol>
                        <MinPurchaseInput
                          type="number"
                          step="0.01"
                          min="0"
                          max="500"
                          value={formData.pickupMinAmount}
                          onChange={handleMinPurchaseChange('pickupMinAmount')}
                          onBlur={handleMinPurchaseBlur('pickupMinAmount')}
                          onKeyDown={handleMinPurchaseKeyDown(
                            'pickupMinAmount',
                          )}
                          onClick={handleMinPurchaseClick('pickupMinAmount')}
                          readOnly={!pickupInputEnabled}
                          title="Use arrow keys ↑↓ to adjust value (Shift + arrow for $10 increments)"
                        />
                      </MinPurchaseInputContainer>
                      <MinPurchaseLabel>
                        {t('Onboarding.branchStore.minPurchaseAmount')}
                      </MinPurchaseLabel>
                    </MinPurchaseContainer>
                  </DeliveryMethodRow>

                  <DeliveryMethodRow
                    className={
                      formData.hasDelivery ? 'has-expandable-content' : ''
                    }>
                    <DeliveryMethodRowContent>
                      <CheckboxOption>
                        <CheckboxInput
                          type="checkbox"
                          name="hasDelivery"
                          checked={formData.hasDelivery}
                          onChange={handleInputChange('hasDelivery')}
                        />
                        <CheckboxLabel>
                          {t('Onboarding.branchStore.delivery')}
                        </CheckboxLabel>
                      </CheckboxOption>
                      <MinPurchaseContainer>
                        <MinPurchaseInputContainer
                          disabled={!deliveryInputEnabled}
                          onClick={handleMinPurchaseClick('deliveryMinAmount')}>
                          <DollarSymbol
                            disabled={!deliveryInputEnabled}
                            readOnly={!deliveryInputEnabled}>
                            $
                          </DollarSymbol>
                          <MinPurchaseInput
                            type="number"
                            step="0.01"
                            min="0"
                            max="500"
                            value={formData.deliveryMinAmount}
                            onChange={handleMinPurchaseChange(
                              'deliveryMinAmount',
                            )}
                            onBlur={handleMinPurchaseBlur('deliveryMinAmount')}
                            onKeyDown={handleMinPurchaseKeyDown(
                              'deliveryMinAmount',
                            )}
                            onClick={handleMinPurchaseClick(
                              'deliveryMinAmount',
                            )}
                            readOnly={!deliveryInputEnabled}
                            title="Use arrow keys ↑↓ to adjust value (Shift + arrow for $10 increments)"
                          />
                        </MinPurchaseInputContainer>
                        <MinPurchaseLabel>
                          {t('Onboarding.branchStore.minPurchaseAmount')}
                        </MinPurchaseLabel>
                      </MinPurchaseContainer>
                    </DeliveryMethodRowContent>

                    {formData.hasDelivery && (
                      <CollapsibleDeliveryAreaSection
                        isVisible={showDeliveryArea}>
                        <FieldGroup className="miles-around">
                          <FormFieldLabel>
                            {t('Onboarding.branchStore.deliveryArea')}
                          </FormFieldLabel>
                          <MinPurchaseContainer>
                            <MinPurchaseInputContainer className="miles-around">
                              <MinPurchaseInput
                                className="miles-around"
                                type="number"
                                min="1"
                                max="50"
                                value={formData.deliveryRadius}
                                onChange={handleInputChange('deliveryRadius')}
                              />
                            </MinPurchaseInputContainer>
                            <MinPurchaseLabel>
                              {t('Onboarding.branchStore.milesAround')}
                            </MinPurchaseLabel>
                          </MinPurchaseContainer>
                        </FieldGroup>
                      </CollapsibleDeliveryAreaSection>
                    )}
                  </DeliveryMethodRow>
                </DeliveryMethodContainer>
                {errors.deliveryMethods && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errors.deliveryMethods}</ErrorText>
                  </ErrorContainer>
                )}
              </FieldGroup>
            </FormField>

            {/* Branch Store Phone - Right Column */}
            <FormField>
              <FieldGroup>
                <FieldLabel>
                  {t('Onboarding.branchStore.phoneNumber')}
                </FieldLabel>
                <StyledInput
                  type="tel"
                  placeholder={t('Onboarding.branchStore.phonePlaceholder')}
                  name="branchStorePhone"
                  value={formData.branchStorePhone}
                  onChange={handleInputChange('branchStorePhone')}
                  maxLength="15"
                  pattern="[0-9]*"
                  inputMode="numeric"
                />
                {errors.branchStorePhone && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errors.branchStorePhone}</ErrorText>
                  </ErrorContainer>
                )}
                <HelperText>
                  {t('Onboarding.branchStore.phoneHelper')}
                </HelperText>
              </FieldGroup>
            </FormField>
          </FormGrid>

          <NavigationSection>
            <BackButton onClick={onBack}>
              {t('Onboarding.branchStore.back')}
            </BackButton>
            <NextButton type="submit" disabled={isLoading}>
              {isLoading
                ? t('Onboarding.branchStore.saving')
                : isLastBranch
                ? t('Onboarding.branchStore.saveFinish')
                : t('Onboarding.branchStore.nextBranchStore')}
            </NextButton>
          </NavigationSection>
        </Form>
      </OnboardingFormContainer>

      {/* Modal de confirmación para limpiar dirección */}
      <ModalOverlay isVisible={showClearModal} onClick={cancelClearAddress}>
        <ModalContainer
          isVisible={showClearModal}
          onClick={(e) => e.stopPropagation()}>
          <ModalTitle>{t('Onboarding.modal.clearAddressTitle')}</ModalTitle>
          <ModalMessage>
            {t('Onboarding.modal.clearAddressMessage')}
            <br />• {t('Onboarding.modal.clearAddressItem1')}
            <br />• {t('Onboarding.modal.clearAddressItem2')}
            <br />• {t('Onboarding.modal.clearAddressItem3')}
            <br />
            <br />
            {t('Onboarding.modal.clearAddressWarning')}
          </ModalMessage>
          <ModalActions>
            <ModalCancelButton onClick={cancelClearAddress}>
              {t('Onboarding.modal.cancel')}
            </ModalCancelButton>
            <ModalConfirmButton onClick={confirmClearAddress}>
              {t('Onboarding.modal.clearAll')}
            </ModalConfirmButton>
          </ModalActions>
        </ModalContainer>
      </ModalOverlay>
    </OnboardingBoxStep2>
  );
};
